import Sidebar from "./Sidebar";
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Topbar from "./Topbar";
import toast, { Toaster } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { BASE_URL, getWithTokenAPI, putWithTokenAPI } from "../../util/Api";
import '../../styles/adminprofile.css';
import Swal from 'sweetalert2';
import { setAdminData } from "../../redux/admindaetails";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";

// Icons
const ProfileIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
    </svg>
);

const SecurityIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
    </svg>
);

const CameraIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
        <circle cx="12" cy="13" r="4"></circle>
    </svg>
);

const EditIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
    </svg>
);

const SaveIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
        <polyline points="17 21 17 13 7 13 7 21"></polyline>
        <polyline points="7 3 7 8 15 8"></polyline>
    </svg>
);

const CancelIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M19 6L6 19M6 6l13 13"></path>
    </svg>
);

const AdminProfile = () => {
    const [activeTab, setActiveTab] = useState("profile");
    const [admin, setAdmin] = useState({});
    const [imagePreview, setImagePreview] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const [editableAdmin, setEditableAdmin] = useState({ name: "" });
    const [profileImage, setProfileImage] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [passwords, setPasswords] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
    });

    const user = useSelector((state) => state.user);
    const token = user?.userId?.accessToken;
    const adminId = localStorage.getItem("matrimony_userId");
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch()

    // Fetch Admin Details
    const fetchAdmin = async () => {
        try {
            const res = await getWithTokenAPI(token, `/admin/get_by_id/${adminId}`);
            setAdmin(res?.data);
            setEditableAdmin({ name: res?.data?.name || "" });
            setImagePreview(res?.data?.profileImage || "");
        } catch {
            toast.error("Unable to fetch admin details");
        }
    };

    useEffect(() => {
        fetchAdmin();
    }, []);

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setProfileImage(file);
        setImagePreview(URL.createObjectURL(file));

        const token = localStorage.getItem('matrimony_token');
        const adminId = localStorage.getItem('matrimony_userId');

        if (!token) {
            Swal.fire({
                icon: 'error',
                title: 'Authentication Error',
                text: 'Authentication token not found. Please login again.',
                confirmButtonColor: '#3b82f6'
            });
            return;
        }

        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await fetch(
                `${BASE_URL}/document/upload?adminId=${adminId}&type=media`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                    body: formData,
                }
            );
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Upload failed with status ${response.status}`);
            }
            const result = await response.json();
            let imageUrl = '';
            if (Array.isArray(result.data) && result.data.length > 0 && result.data[0].url) {
                imageUrl = result.data[0].url;
            } else {
                throw new Error("No image URL returned from API");
            }
            setAdmin(prev => ({ ...prev, profileImage: imageUrl }));
            setImagePreview(imageUrl);

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Image uploaded successfully!',
                timer: 2000,
                showConfirmButton: false
            });

        } catch (error) {
            console.error('Image upload error:', error);
            Swal.fire({
                icon: 'error',
                title: 'Upload Failed',
                text: error.message || 'Something went wrong during upload',
                confirmButtonColor: '#3b82f6'
            });
        }
    };

    const handleSaveProfile = async () => {
        try {
            const payload = {
                profileImage: admin.profileImage,
                name: editableAdmin.name
            };
            const res = await putWithTokenAPI(token, `/admin/update/${adminId}`, payload);
            if (res.success) {
                toast.success("Profile updated successfully");
                setIsEditing(false);
                dispatch(setAdminData(res.updated_admin));
                fetchAdmin();
            }
        } catch {
            toast.error("Profile update failed");
        }
    };

    const handleCancelEdit = () => {
        setEditableAdmin({ name: admin.name || "" });
        setIsEditing(false);
    };

    // Handle Password Change
    const handleChangePassword = async () => {
        const { newPassword, confirmPassword } = passwords;
        if (!newPassword || !confirmPassword)
            return toast.error("All fields required");
        if (newPassword !== confirmPassword)
            return toast.error("Passwords do not match");
        const payload = {
            email: admin.email,
            new_password: newPassword
        }

        try {
            const res = await putWithTokenAPI(token, `/admin/update/${adminId}`, payload);
            if (res.success) {
                setActiveTab("profile")
                toast.success("Password updated successfully");
            }
            setPasswords({ newPassword: "", confirmPassword: "" });
        } catch {
            toast.error("Wrong current password");
        }
    };

    const [activeSection, setActiveSection] = useState("subscriptions");
    const handleSectionChange = (section) => {
        setActiveSection(section);

        switch (section) {
            case "dashboard":
                navigate("/admin/dashboard");
                break;
            case "users":
                navigate("/admin/user-management");
                break;
            case "approvals":
                navigate("/admin/content-approvals");
                break;
            case "reports":
                navigate("/admin/reports-moderation");
                break;
            case "subscriptions":
                navigate("/admin/subscription-plans");
                break;
            case "cms":
                navigate("/admin/content-management");
                break;
            case "payments":
                navigate("/admin/payment-list");
                break;
            case "user-profile":
                if (!location.pathname.includes("/admin/user-profile/")) {
                    navigate("/admin/user-management");
                }
                break;
            default:
                navigate("/admin/dashboard");
        }
    };

    return (
        <div className="admin-layout-container">
            <Topbar />
            <Toaster position="top-right" />
            <div className="admin-main-area">
                <Sidebar
                    activeSection={activeSection}
                    setActiveSection={handleSectionChange}
                />

                <div className="admin-content-area">
                    <div className="admin-scrollable-content">
                        <div className="admin-content-area modern-profile">
                            <div className="profile-layout">
                                <div className="profile-sidebar">
                                    <div className="sidebar-nav">
                                        <button
                                            className={`nav-item ${activeTab === "profile" ? "active" : ""}`}
                                            onClick={() => setActiveTab("profile")}
                                        >
                                            <ProfileIcon />
                                            <span>Profile Details</span>
                                        </button>
                                        <button
                                            className={`nav-item ${activeTab === "password" ? "active" : ""}`}
                                            onClick={() => setActiveTab("password")}
                                        >
                                            <SecurityIcon />
                                            <span>Security & Password</span>
                                        </button>
                                    </div>
                                </div>

                                {/* MAIN CONTENT AREA */}
                                <div className="profile-main-content">
                                    {activeTab === "profile" && (
                                        <div className="content-section">
                                            <div className="section-header">
                                                <h2 className="section-title">Profile Information</h2>
                                                {!isEditing ? (
                                                    <button
                                                        className="edit-profile-btn black-white-btn"
                                                        onClick={() => setIsEditing(true)}
                                                    >
                                                        <EditIcon />
                                                        Edit Profile
                                                    </button>
                                                ) : (
                                                    <div className="edit-actions">
                                                        <button
                                                            className="save-btn black-white-btn"
                                                            onClick={handleSaveProfile}
                                                        >
                                                            <SaveIcon />
                                                            Save Changes
                                                        </button>
                                                        <button
                                                            className="cancel-btn black-white-btn"
                                                            onClick={handleCancelEdit}
                                                        >
                                                            <CancelIcon />
                                                            Cancel
                                                        </button>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="profile-content-wrapper">
                                                <div className="profile-image-section">
                                                    <div className="image-upload-container">
                                                        <div className="avatar-wrapper">
                                                            <img
                                                                src={imagePreview || "/no-user.png"}
                                                                alt="profile"
                                                                className="profile-avatar"
                                                            />
                                                            {
                                                                isEditing && (
                                                                    <label htmlFor="avatar-upload" className="avatar-edit-btn">
                                                                        <CameraIcon />
                                                                        <input
                                                                            id="avatar-upload"
                                                                            type="file"
                                                                            accept="image/*"
                                                                            onChange={handleImageChange}
                                                                            className="hidden-input"
                                                                        />
                                                                    </label>
                                                                )
                                                            }

                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="profile-details-section">
                                                    <div className="detail-group">
                                                        <label className="detail-label">Full Name</label>
                                                        {isEditing ? (
                                                            <input
                                                                type="text"
                                                                value={editableAdmin.name}
                                                                className="modern-input edit-input"
                                                                onChange={(e) => setEditableAdmin({ ...editableAdmin, name: e.target.value })}
                                                            />
                                                        ) : (
                                                            <div className="detail-value">{admin?.name || "Not provided"}</div>
                                                        )}
                                                    </div>

                                                    {/* Email (NOT editable) */}
                                                    <div className="detail-group">
                                                        <label className="detail-label">Email Address</label>
                                                        <div className="detail-value">{admin?.email || "Not provided"}</div>
                                                    </div>

                                                    {/* Role (NOT editable) */}
                                                    <div className="detail-group">
                                                        <label className="detail-label">Role</label>
                                                        <div className="detail-value">{admin?.role || "Admin"}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {activeTab === "password" && (
                                        <div className="content-section">
                                            <h2 className="section-title">Change Password</h2>
                                            {/* <div className="security-card glassmorphic-card">
                                                <div className="password-form">
                                                    <div className="input-group">
                                                        <label className="input-label">New Password</label>

                                                        <input
                                                            type="password"
                                                            className="modern-input"
                                                            placeholder="Enter new password"
                                                            value={passwords.newPassword}
                                                            onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                                                        />

                                                    </div>
                                                    <div className="input-group">
                                                        <label className="input-label">Confirm New Password</label>

                                                        <input
                                                            type="password"
                                                            className="modern-input"
                                                            placeholder="Confirm new password"
                                                            value={passwords.confirmPassword}
                                                            onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                                                        />

                                                    </div>
                                                    <button className="black-white-btn update-password-btn" onClick={handleChangePassword}>
                                                        Update Password
                                                    </button>
                                                </div>
                                            </div> */}
                                            <div className="security-card glassmorphic-card">
                                                <div className="password-form">

                                                    {/* New Password */}
                                                    <div className="input-group">
                                                        <label className="input-label">New Password</label>

                                                        <div className="input-wrapper">
                                                            <input
                                                                type={showPassword ? "text" : "password"}
                                                                className="modern-input"
                                                                placeholder="Enter new password"
                                                                value={passwords.newPassword}
                                                                onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                                                            />

                                                    
                                                            <span
                                                                onClick={() => setShowPassword(!showPassword)}
                                                                 className="password-eye"
                                                            >
                                                                {showPassword ? <AiOutlineEyeInvisible size={22} /> : <AiOutlineEye size={22} />}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Confirm Password */}
                                                    <div className="input-group">
                                                        <label className="input-label">Confirm New Password</label>

                                                        <div className="input-wrapper">
                                                            <input
                                                                type={showConfirm ? "text" : "password"}
                                                                className="modern-input"
                                                                placeholder="Confirm new password"
                                                                value={passwords.confirmPassword}
                                                                onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                                                            />

                                                            <span
                                                                onClick={() => setShowConfirm(!showConfirm)}
                                                                className="password-eye"
                                                            >
                                                                {showConfirm ? <AiOutlineEyeInvisible size={22} /> : <AiOutlineEye size={22} />}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <button className="black-white-btn update-password-btn" onClick={handleChangePassword}>
                                                        Update Password
                                                    </button>
                                                </div>
                                            </div>

                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default AdminProfile;